import type { PulseStory } from "@altair/shared/types";
import { PAYER_COLORS, DEFAULT_PAYER_COLOR } from "./payer-colors";
import { ShareBar } from "./ShareBar";
import { extractRelatedLinks } from "@/lib/news/related-links";

/**
 * Full-bleed story article used both in the list-mode pulse page and the
 * single-story deep-link page (`/news/:date/:slug`). Extracted verbatim from
 * InsightDetailPage.tsx — no visual change.
 */

interface StoryBlockProps {
  story: PulseStory;
  date: string;
  slug: string;
  index: number;
  /**
   * Heading level for the headline. "h1" on the single-story page (the story
   * is the page's primary entity); defaults to "h2" when nested under a pulse's
   * date <h1> so each page keeps exactly one <h1>.
   */
  headingLevel?: "h1" | "h2";
}

export function StoryBlock({
  story,
  date,
  slug,
  index,
  headingLevel = "h2",
}: StoryBlockProps) {
  const hasPracticeImpact =
    story.practiceImpact && story.practiceImpact.length > 0;
  const Heading = headingLevel;
  const relatedLinks = extractRelatedLinks(story);

  return (
    <article>
      {/* Headline */}
      <Heading
        style={{
          fontFamily: "'Space Grotesk', sans-serif",
          fontSize: "clamp(19px, 2.5vw, 22px)",
          fontWeight: 600,
          letterSpacing: "-0.01em",
          color: "hsl(var(--foreground))",
          lineHeight: 1.35,
          margin: 0,
        }}
      >
        {story.headline}
      </Heading>

      {/* Source + payer tags */}
      {(story.source || story.payers.length > 0) && (
        <div
          className="flex items-center gap-2 flex-wrap"
          style={{ marginTop: 8 }}
        >
          {story.source &&
            (story.sourceUrl ? (
              <a
                href={story.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: 13,
                  color: "hsl(var(--foreground) / 0.45)",
                  textDecoration: "underline",
                  textDecorationColor: "hsl(var(--foreground) / 0.15)",
                  textUnderlineOffset: 3,
                  transition: "color 150ms, text-decoration-color 150ms",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = "hsl(var(--foreground) / 0.70)";
                  e.currentTarget.style.textDecorationColor =
                    "hsl(var(--foreground) / 0.40)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = "hsl(var(--foreground) / 0.45)";
                  e.currentTarget.style.textDecorationColor =
                    "hsl(var(--foreground) / 0.15)";
                }}
              >
                {story.source}
                {story.sourceDate ? ` · ${story.sourceDate}` : ""}
              </a>
            ) : (
              <span
                style={{
                  fontSize: 13,
                  color: "hsl(var(--foreground) / 0.45)",
                }}
              >
                {story.source}
                {story.sourceDate ? ` · ${story.sourceDate}` : ""}
              </span>
            ))}
          {story.payers.length > 0 && (
            <div className="flex items-center gap-1">
              {story.payers.map((payer) => {
                const pc = PAYER_COLORS[payer] || DEFAULT_PAYER_COLOR;
                return (
                  <span
                    key={payer}
                    style={{
                      fontSize: 11,
                      fontWeight: 500,
                      color: pc.color,
                      background: pc.bg,
                      padding: "2px 7px",
                      borderRadius: 4,
                    }}
                  >
                    {payer}
                  </span>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Summary */}
      <div
        style={{
          fontSize: 15,
          lineHeight: 1.7,
          color: "hsl(var(--foreground) / 0.75)",
          marginTop: 16,
        }}
      >
        {story.summary.split("\n\n").map((para, i) => (
          <p key={i} style={{ marginBottom: i === 0 ? 12 : 0 }}>
            {para}
          </p>
        ))}
      </div>

      {/* Practice Impact */}
      {hasPracticeImpact && (
        <div
          style={{
            marginTop: 20,
            background: "hsl(38 60% 50% / 0.04)",
            border: "1px solid hsl(38 60% 50% / 0.10)",
            borderRadius: 10,
            padding: "16px 20px",
          }}
        >
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "hsl(38 50% 38%)",
              marginBottom: 10,
              letterSpacing: "0.01em",
            }}
          >
            Practice Impact
          </div>
          <ul
            style={{
              margin: 0,
              padding: 0,
              listStyle: "none",
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {story.practiceImpact!.map((bullet, i) => (
              <li
                key={i}
                className="flex items-start gap-2.5"
                style={{
                  fontSize: 14,
                  lineHeight: 1.55,
                  color: "hsl(var(--foreground) / 0.65)",
                }}
              >
                <span
                  style={{
                    marginTop: 8,
                    width: 5,
                    height: 5,
                    borderRadius: "50%",
                    background: "hsl(var(--foreground) / 0.25)",
                    flexShrink: 0,
                  }}
                />
                {bullet}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Related references — internal links into the evergreen docs cluster.
          All targets are allowlisted to existing docs (see related-links.ts),
          so these never 404. Crawlable <a href> for link-equity flow. */}
      {relatedLinks.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: "hsl(var(--foreground) / 0.40)",
              letterSpacing: "0.01em",
              textTransform: "uppercase",
              marginBottom: 8,
            }}
          >
            Related references
          </div>
          <div className="flex flex-wrap gap-2">
            {relatedLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                style={{
                  fontSize: 13,
                  color: "hsl(var(--foreground) / 0.60)",
                  textDecoration: "none",
                  border: "1px solid hsl(var(--foreground) / 0.12)",
                  borderRadius: 9999,
                  padding: "4px 12px",
                  transition: "color 150ms, border-color 150ms",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = "hsl(var(--foreground))";
                  e.currentTarget.style.borderColor = "hsl(var(--primary) / 0.40)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = "hsl(var(--foreground) / 0.60)";
                  e.currentTarget.style.borderColor =
                    "hsl(var(--foreground) / 0.12)";
                }}
              >
                {link.label} →
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Share bar */}
      <ShareBar
        date={date}
        slug={slug}
        index={index}
        headline={story.headline}
      />
    </article>
  );
}
